import { parsePaymentInfos } from 'modules/deal/components/section/payment/stripePaymentInfo';
export function roundMoney(value) {
    return Math.round((value + Number.EPSILON) * 100) / 100;
}
function percentOf(line) {
    if (line.percent == null) {
        return null;
    }
    const percent = Number(line.percent);
    return Number.isFinite(percent) ? percent : null;
}
/**
 * Recompute the amount of every open percent-based payment from the deal
 * total. Completed payments keep what was actually charged. The last
 * percent-based payment absorbs the rounding cent.
 *
 * Mirrors the backend (`lib/util/dealPaymentSchedule.ts`), keep both in sync.
 */
export function applyPercentPaymentAmounts(payments, total) {
    const eligible = payments
        .map((line, index) => ({ index, percent: percentOf(line) }))
        .filter((entry) => entry.percent !== null && payments[entry.index].status !== 'COMPLETED');
    if (eligible.length === 0) {
        return payments;
    }
    const dealTotal = Number(total) || 0;
    const target = roundMoney((dealTotal * eligible.reduce((sum, entry) => sum + entry.percent, 0)) / 100);
    const amounts = new Map();
    let allocated = 0;
    eligible.forEach((entry, position) => {
        const isLast = position === eligible.length - 1;
        const amount = isLast
            ? roundMoney(target - allocated)
            : roundMoney((dealTotal * entry.percent) / 100);
        allocated = roundMoney(allocated + amount);
        amounts.set(entry.index, amount);
    });
    return payments.map((line, index) => amounts.has(index) ? Object.assign(Object.assign({}, line), { amount: amounts.get(index) }) : line);
}
/**
 * Share of the total shown for a payment: the percent typed when the
 * payment follows the total, otherwise its fixed amount over the total.
 */
export function paymentPercent(payment, total) {
    const percent = percentOf(payment);
    if (percent !== null && payment.status !== 'COMPLETED') {
        return percent;
    }
    return total ? Math.round((payment.amount / total) * 10000) / 100 : 0;
}
/**
 * What is left to schedule: the deal total minus every payment amount.
 */
export function paymentScheduleBalance(payments, total) {
    return roundMoney((Number(total) || 0) -
        payments.reduce((sum, payment) => sum + (Number(payment.amount) || 0), 0));
}
/**
 * Bring the schedule back to the deal total by adjusting the last open
 * payment. When every payment follows the total, the last percent becomes
 * the remainder to 100 %, so the schedule keeps following the total.
 */
export function settlePaymentScheduleBalance(payments, total) {
    const balance = paymentScheduleBalance(payments, total);
    if (Math.abs(balance) < 0.005) {
        return payments;
    }
    const lastIndex = payments
        .map((payment) => payment.status !== 'COMPLETED')
        .lastIndexOf(true);
    if (lastIndex < 0) {
        return payments;
    }
    const allPercent = payments.every((payment) => payment.status !== 'COMPLETED' && percentOf(payment) !== null);
    if (allPercent) {
        const others = payments.reduce((sum, payment, index) => { var _a; return index === lastIndex ? sum : sum + ((_a = percentOf(payment)) !== null && _a !== void 0 ? _a : 0); }, 0);
        const percent = Math.round((100 - others) * 10000) / 10000;
        return applyPercentPaymentAmounts(payments.map((payment, index) => index === lastIndex ? Object.assign(Object.assign({}, payment), { percent }) : payment), total);
    }
    return payments.map((payment, index) => index === lastIndex
        ? Object.assign(Object.assign({}, payment), { amount: roundMoney(Number(payment.amount) + balance), percent: null }) : payment);
}
/**
 * A payment settled through Stripe, Mews or a store upsell. Its amount and
 * status are what the processor charged; the server ignores edits to them.
 * A payment marked completed by hand has no infos and stays editable.
 */
export function isProcessedPayment(payment) {
    const infos = parsePaymentInfos(payment.infos);
    if (infos.kind === 'upsell') {
        return true;
    }
    return payment.status === 'COMPLETED' && Object.keys(infos).length > 0;
}
/**
 * A store upsell not paid yet. Its amount is what the extras would add, so it
 * is not part of the deal total until it is paid.
 */
export function isPendingUpsellPayment(payment) {
    return (payment.status !== 'COMPLETED' &&
        parsePaymentInfos(payment.infos).kind === 'upsell');
}
/**
 * The payments a split leaves untouched: what was already paid and upsells.
 */
function isKeptBySplit(payment) {
    return payment.status === 'COMPLETED' || isProcessedPayment(payment);
}
/**
 * Whether applying a split would discard something the seller entered.
 */
export function hasOpenPaymentData(payments) {
    return payments
        .filter((payment) => !isKeptBySplit(payment))
        .some((payment) => {
        var _a;
        return Number(payment.amount) !== 0 ||
            percentOf(payment) !== null ||
            !!payment.date ||
            !!((_a = payment.description) === null || _a === void 0 ? void 0 : _a.trim());
    });
}
function startOfDay(value) {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
}
function addDays(value, days) {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate() + days);
}
/**
 * Due date of the last payment, computed once. A date that falls in the past
 * is brought back to today and reported, so the seller can see it happened.
 */
export function resolvePaymentDueDate(rule, days, plannedDateStart, now = new Date()) {
    const today = startOfDay(now);
    if (rule === 'FROM_TODAY') {
        return { clamped: false, date: addDays(today, days) };
    }
    if (rule !== 'BEFORE_EVENT' || !plannedDateStart) {
        return { clamped: false, date: null };
    }
    // Planned dates are stored as the local midnight of the chosen day.
    const event = startOfDay(new Date(plannedDateStart));
    if (Number.isNaN(event.getTime())) {
        return { clamped: false, date: null };
    }
    const date = addDays(event, -days);
    return date < today
        ? { clamped: true, date: today }
        : { clamped: false, date };
}
const SPLIT_LABELS = {
    en: {
        balance: 'Balance',
        deposit: 'Deposit',
        payment: 'Payment',
        single: 'Single payment'
    },
    es: {
        balance: 'Saldo',
        deposit: 'Depósito',
        payment: 'Pago',
        single: 'Pago único'
    },
    fr: {
        balance: 'Solde',
        deposit: 'Dépôt',
        payment: 'Paiement',
        single: 'Paiement unique'
    }
};
/**
 * Replace the open payments with a schedule following the given shares. Paid
 * payments and upsells are kept, and only what is left of the total is split.
 * Lines are percent-based so they follow the total; the last one takes the
 * remainder because 33.3333 % three times would leave a cent behind.
 *
 * Returns null when nothing is left to split.
 */
export function buildPaymentSplit(options) {
    var _a, _b;
    const { deposit, dueDate, language, payments, shares, total } = options;
    const dealTotal = Number(total) || 0;
    const completed = payments.filter((payment) => isKeptBySplit(payment) && !isPendingUpsellPayment(payment));
    const upsells = payments.filter(isPendingUpsellPayment);
    const paid = completed.reduce((sum, payment) => sum + (Number(payment.amount) || 0), 0);
    // Without a total yet, the percents are still valid: amounts follow later.
    const remaining = roundMoney(dealTotal - paid);
    if (completed.length > 0 && remaining <= 0) {
        return null;
    }
    const remainingPercent = completed.length > 0 ? (remaining / dealTotal) * 100 : 100;
    const weight = shares.reduce((sum, share) => sum + share, 0);
    const count = shares.length;
    const labels = (_a = SPLIT_LABELS[language !== null && language !== void 0 ? language : '']) !== null && _a !== void 0 ? _a : SPLIT_LABELS.en;
    const hasPaid = completed.length > 0;
    const today = startOfDay((_b = options.now) !== null && _b !== void 0 ? _b : new Date());
    // Before anything is paid, the first line is due at signing.
    const undated = hasPaid || count === 1 ? 0 : 1;
    const dated = count - undated;
    let allocated = 0;
    const lines = shares.map((share, index) => {
        const isLast = index === count - 1;
        const percent = isLast
            ? Math.round((remainingPercent - allocated) * 10000) / 10000
            : Math.round(((remainingPercent * share) / weight) * 10000) / 10000;
        allocated += percent;
        const isDeposit = deposit && !hasPaid && count > 1 && index === 0;
        let description = `${labels.payment} ${index + 1}`;
        if (isDeposit) {
            description = labels.deposit;
        }
        else if (count === 1 && !hasPaid) {
            description = labels.single;
        }
        else if (isLast) {
            description = labels.balance;
        }
        let date = null;
        if (dueDate && index >= undated) {
            const span = Math.round((startOfDay(dueDate).getTime() - today.getTime()) / 86400000);
            const step = (index - undated + 1) / dated;
            date = addDays(today, Math.round(span * step)).toISOString();
        }
        return {
            id: '',
            date,
            description,
            createdAt: '',
            updatedAt: '',
            status: index === 0 && !hasPaid ? 'ACTIVE' : 'INACTIVE',
            amount: 0,
            percent,
            isDeposit: isDeposit ? true : null
        };
    });
    const scheduled = applyPercentPaymentAmounts([...completed, ...lines], dealTotal);
    return [
        ...(hasPaid
            ? settlePaymentScheduleBalance(scheduled, dealTotal)
            : scheduled),
        ...upsells
    ];
}
